import { useTranslations } from "next-intl";
import { Transaction } from "./services/transactionService";
import { authStorage } from "./lib/authStorage";
import { TradeVariant } from "./types/domain";

export const PAGE_SIZE = 10;

export const parseNum = (v: string) => parseFloat(v.replace(/,/g, "")) || 0;

export const parseMinOrderQuantity = (v: unknown) => {
  const n = Number.parseInt(String(v ?? ""), 10);
  return Number.isFinite(n) && n > 0 ? n : 1;
};

export const ICON_ACCENT = "text-[#3B82F6]";
export const ICON_MUTED = "text-slate-400";

export const typeClassMap: Record<Transaction["type"], string> = {
  Buy: "bg-pink-100 text-pink-600",
  Sell: "bg-sky-100 text-sky-600",
  Exchange: "bg-yellow-100 text-yellow-600",
};

export const statusColorMap: Record<string, { text: string; dot: string }> = {
  Delivered: { text: "text-green-500", dot: "bg-green-500" },
  Completed: { text: "text-blue-500", dot: "bg-blue-500" },
  COMPLETE: { text: "text-blue-500", dot: "bg-blue-500" },
  Cancelled: { text: "text-slate-400", dot: "bg-slate-400" },
  REJECTED: { text: "text-red-500", dot: "bg-red-500" },
  REQUESTED: { text: "text-yellow-600", dot: "bg-yellow-400" },
  APPROVED: { text: "text-green-600", dot: "bg-green-400" },
  default: { text: "text-slate-400", dot: "bg-slate-400" },
};

export const requestTypeClassMap: Record<"Sample" | "Complete", string> = {
  Sample: "bg-amber-100 text-amber-700",
  Complete: "bg-blue-100 text-blue-700",
};

export const NAV_ITEMS = [
  { labelKey: "productCatalog", sectionId: "product-catalog" },
  { labelKey: "aboutUs", sectionId: "about-us-section" },
  { labelKey: "ourVision", sectionId: "our-vision-section" },
  { labelKey: "ourMission", sectionId: "our-mission-section" },
  { labelKey: "contactUs", sectionId: "contact-section" },
] as const;

export type NavLabelKey = (typeof NAV_ITEMS)[number]["labelKey"];

export const NAV_OFFSET = 82;

export const initialBuy = { product: '', quantity: '', fullName: '', gst: '', address: '', pincode: '' };
export const initialSell = { product: '', pricePerKg: '', quantity: '', fullName: '', phone: '', gst: '', paymentDetails: '' };
export const initialEx = { giveProduct: '', giveQuantity: '', receiveProduct: '', receiveQuantity: '', fullName: '', gst: '' };


export const validateBuy = (
  buy: typeof initialBuy,
  t: ReturnType<typeof useTranslations>,
  minQuantity: number
): Record<string, string> => {
  const errs: Record<string, string> = {};
  if (!buy.product.trim()) errs.product = t('errors.selectProduct');
  if (!buy.quantity.trim()) errs.quantity = t('errors.quantityRequired');
  else if (parseNum(buy.quantity) < minQuantity) errs.quantity = t('errors.quantityInvalid');
  else if (parseNum(buy.quantity) % minQuantity !== 0) errs.quantity = t('errors.quantityMultiple', { min: minQuantity, ex1: minQuantity * 2, ex2: minQuantity * 3 });
  if (!buy.fullName.trim()) errs.fullName = t('errors.fullNameRequired');
  if (!buy.address.trim()) errs.address = t('errors.addressRequired');
  if (!buy.pincode.trim()) {
    errs.pincode = t('errors.pincodeRequired');
  } else if (!/^\d{4,10}$/.test(buy.pincode.trim())) {
    errs.pincode = t('errors.pincodeInvalid');
  }
  return errs;
};

export const validateSell = (sell: typeof initialSell, t: ReturnType<typeof useTranslations>, minQuantity: number): Record<string, string> => {
  const errs: Record<string, string> = {};
  if (!sell.product.trim()) errs.product = t('errors.selectProduct');
  if (!sell.quantity.trim()) errs.quantity = t('errors.quantityRequired');
  else if (parseNum(sell.quantity) <= 0) errs.quantity = t('errors.quantityInvalid');
  else if (minQuantity > 1 && parseNum(sell.quantity) % minQuantity !== 0) errs.quantity = t('errors.quantityMultiple', { min: minQuantity, ex1: minQuantity * 2, ex2: minQuantity * 3 });
  if (!sell.pricePerKg.trim() || parseNum(sell.pricePerKg) <= 0) errs.pricePerKg = t('errors.priceInvalid');
  if (!sell.fullName.trim()) errs.fullName = t('errors.fullNameRequired');
  if (!sell.phone || !sell.phone.trim()) {
    errs.phone = t('errors.phoneRequired');
  } else if (sell.phone.trim().length !== 10) {
    errs.phone = t('errors.phoneLength');
  } else if (!/^[6-9]/.test(sell.phone.trim())) {
    errs.phone = t('errors.phoneInvalid');
  }
  return errs;
};
export const validateExchange = (
  ex: typeof initialEx,
  t: ReturnType<typeof useTranslations>,
  giveMin: number
): Record<string, string> => {
  const errs: Record<string, string> = {};

  if (!ex.giveProduct.trim())
    errs.giveProduct = t('errors.selectProduct');

  if (!ex.giveQuantity.trim())
    errs.giveQuantity = t('errors.quantityRequired');
  else if (parseNum(ex.giveQuantity) <= 0)
    errs.giveQuantity = t('errors.quantityInvalid');
  else if (giveMin > 1 && parseNum(ex.giveQuantity) % giveMin !== 0)
    errs.giveQuantity = t('errors.quantityMultiple', { min: giveMin, ex1: giveMin * 2, ex2: giveMin * 3 });

  if (!ex.receiveProduct.trim())
    errs.receiveProduct = t('errors.selectProduct');

  if (!ex.fullName.trim())
    errs.fullName = t('errors.fullNameRequired');

  return errs;
};

export const syncUserDetailsToStorage = (fields: Record<string, string>) => {
  authStorage.mergeUserDetails(fields);
};

export const syncBuyField = (key: keyof typeof initialBuy, value: string) => {
  if (key === 'fullName') {
    syncUserDetailsToStorage({ name: value, fullName: value });
  }
  if (key === 'address') {
    syncUserDetailsToStorage({ address: value });
  }
  if (key === 'pincode') {
    syncUserDetailsToStorage({ pinCode: value, pincode: value });
  }
  if (key === 'gst') {
    syncUserDetailsToStorage({ gstNumber: value });
  }
};

export const syncSellField = (key: keyof typeof initialSell, value: string) => {
  if (key === 'fullName') {
    syncUserDetailsToStorage({ name: value, fullName: value });
  }
  if (key === 'phone') {
    syncUserDetailsToStorage({ phoneNumber: value, phone: value, mobile: value });
  }
  if (key === 'gst') {
    syncUserDetailsToStorage({ gstNumber: value });
  }
  if (key === 'paymentDetails') {
    syncUserDetailsToStorage({ bankDetails: value });
  }
};

export const syncExchangeField = (key: keyof typeof initialEx, value: string) => {
  if (key === 'fullName') {
    syncUserDetailsToStorage({ name: value, fullName: value });
  }
  if (key === 'gst') {
    syncUserDetailsToStorage({ gstNumber: value });
  }
};


export const WHATSAPP_NUMBER = "919111113844";

export const CLS = {
  input:
    "w-full mt-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#3B82F6]/40 cursor-pointer",
  inputErr:
    "w-full mt-1 rounded-lg border border-red-300 bg-white px-3 py-2 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-red-400/40",
  label: "block text-xs font-medium text-slate-600 mb-0.5",
  sectionTitle:
    "flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2",
  sidebarTitle: "text-lg font-bold text-white",
  sidebarDesc: "text-xs text-white/80 leading-snug",
  closeBtn: "text-slate-400 hover:text-slate-600",
  error: "mt-4 text-sm text-red-600",
  primaryBtn:
    "flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-[#3B82F6] text-white font-semibold hover:bg-[#2563EB] disabled:opacity-60 transition-colors cursor-pointer",
};

export const inp = (err?: string) => (err ? CLS.inputErr : CLS.input);

export const MODAL_CONFIG: Record<
  TradeVariant,
  { size: string; sidebarWidth: string; padding: string; footerPadding: string }
> = {
  buy: {
    size: "max-w-3xl",
    sidebarWidth: "w-[30%]",
    padding: "p-6 pt-8",
    footerPadding: "p-6 pt-3",
  },
  sell: {
    size: "max-w-3xl",
    sidebarWidth: "w-[30%]",
    padding: "p-6 pt-8",
    footerPadding: "p-6 pt-3",
  },
  exchange: {
    size: "max-w-4xl",
    sidebarWidth: "w-[30%]",
    padding: "p-6 pt-8",
    footerPadding: "p-6 pt-3",
  },
};
